import React, { useEffect, useRef, useState } from 'react';

// Retain page geometry for scroll synchronisation, but at most three decoded
// images/PDF canvases and annotation buffers, regardless of document length.
export default function LiveClassPageStack({ pages, currentPage = 1, width, scrollRef, pageRefs, renderPage, renderOverlay }) {
  const [visiblePage, setVisiblePage] = useState(currentPage);
  const frame = useRef(null);

  useEffect(() => {
    const target = Math.max(1, Math.min(pages.length, currentPage));
    setVisiblePage(target);
    pageRefs.current[target]?.scrollIntoView?.({ behavior: 'auto', block: 'start' });
  }, [currentPage, pages.length, pageRefs]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return undefined;
    const update = () => {
      frame.current = null;
      const top = container.getBoundingClientRect().top;
      // Use the top of the viewport so differently sized mobile/desktop screens
      // agree about which page is being presented.
      const anchor = top + Math.min(100, container.clientHeight / 3);
      let nearest = 1;
      let distance = Infinity;
      pages.forEach((page, index) => {
        const node = pageRefs.current[index + 1];
        if (!node) return;
        const rect = node.getBoundingClientRect();
        const delta = anchor < rect.top ? rect.top - anchor : Math.max(0, anchor - rect.bottom);
        if (delta < distance) { nearest = index + 1; distance = delta; }
      });
      setVisiblePage(nearest);
    };
    const schedule = () => {
      if (frame.current === null) frame.current = requestAnimationFrame(update);
    };
    container.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      container.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
    };
  }, [pages, pageRefs, scrollRef]);

  return pages.map((page, index) => {
    const number = index + 1;
    const mounted = Math.abs(number - visiblePage) <= 1;
    return (
      <div
        key={number}
        data-document-page={number}
        ref={(node) => {
          if (node) pageRefs.current[number] = node;
          else delete pageRefs.current[number];
        }}
        className="relative flex flex-col items-center justify-start bg-white shadow-2xl rounded-xl overflow-hidden mx-auto max-w-full"
        style={{ width, aspectRatio: `${page.width} / ${page.height}`, flexShrink: 0 }}
      >
        {mounted && <>
          {renderPage(page, number)}
          {renderOverlay(number, page.width, page.height)}
        </>}
      </div>
    );
  });
}

export function boundedCanvasSize(width, height) {
  const w = Number(width) > 0 ? Number(width) : 595;
  const h = Number(height) > 0 ? Number(height) : 842;
  const scale = Math.min(1600 / w, 1600 / h, Math.sqrt(1_000_000 / (w * h)));
  return { width: Math.max(1, Math.floor(w * scale)), height: Math.max(1, Math.floor(h * scale)) };
}

export function LiveClassAnnotationCanvas({ pageNumber, pageWidth, pageHeight, register, ...props }) {
  const canvasRef = useRef(null);
  const size = boundedCanvasSize(pageWidth, pageHeight);
  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = size.width;
    canvas.height = size.height;
    register(pageNumber, canvas);
    return () => {
      register(pageNumber, null);
      // Release backing memory immediately on document/page switch.
      canvas.width = 0;
      canvas.height = 0;
    };
  }, [pageNumber, register, size.width, size.height]);
  return <canvas ref={canvasRef} width={size.width} height={size.height} {...props} />;
}
